import omit from "lodash/omit";
import { defaultFirebaseProjectId } from "../constants";
import {
  configListFilterCacheArgs,
  knownConfigsForFilter,
  mergeConfigsByClientIdCache,
  readConfigListFilterCache,
  writeConfigListFilterCache,
  writeSectionBlocksCache,
} from "../utils/configs-by-client-id-cache";

const ClientConfig = (app) => {
  app
    .post(
      "/client-configuration/save",
      async ({ api, flash, req, res, locals }) => {
        const { body, query = {} } = req;
        const { athlete } = query;

        try {
          const notifyFemaleCycle = body.notifyFemaleCycle === true;
          const { morningBriefSendTimeOverride } = body;
          const clientConfigBody = omit(body, [
            "notifyFemaleCycle",
            "detach",
            "morningBriefSendTimeOverride",
          ]);

          const userToRequest = locals.activeUserToRequest || locals.activeUser;

          const coachAvatar = userToRequest?.avatar?.file ?? null;
          const coachFirebaseProjectId =
            userToRequest?.firebaseProjectId ?? defaultFirebaseProjectId;

          if (body.detach) {
            await api(`client-configurations/${athlete}/detach`, {
              method: "POST",
            });
          }

          // Coach Monitoring's per-client override is a separate backend
          // resource (`coach-monitoring-settings`, not `client-configurations`),
          // but shares this page's single Save button — write it alongside
          // the client-configuration PUT below, only when it actually changed.
          if (morningBriefSendTimeOverride !== undefined) {
            await api(`coach-monitoring-settings/${athlete}`, {
              method: "PUT",
              body: {
                morningBrief: {
                  sendTimeOverride: morningBriefSendTimeOverride,
                },
              },
            });
          }

          await api(`client-configurations/${athlete}`, {
            method: "PUT",
            body: {
              ...clientConfigBody,
              notifyFemaleCycle,
              coachAvatar,
              coachFirebaseProjectId,
            },
          });

          if (notifyFemaleCycle) {
            flash("success", "Configuration saved");
            return res.refreshWith(
              { "modal[type]": "female-cycle-enabled" },
              { url: "back", hash: "#_" }
            );
          }

          flash("success", "Configuration saved");
          res.backDontScroll();
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/client-configuration/reassign-template",
      async ({ api, flash, req, res }) => {
        const { body, query = {} } = req;
        const { athlete } = query;

        try {
          await api(`client-configurations/${athlete}/template`, {
            method: "PUT",
            body: {
              templateId: body.templateId,
              ...(body.resolveConflicts && {
                resolveConflicts: true,
                conflictingSections: body.conflictingSections,
              }),
            },
          });

          flash("success", "Template reassigned");
          return res.refreshWith(
            { "modal[type]": "" },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/client-configuration/create-template",
      async ({ api, flash, req, res }) => {
        const { body } = req;
        const { templateName, sections, isDefault, defaultInsightsLens } =
          body || {};
        try {
          if (!templateName || String(templateName).trim() === "") {
            throw new Error("Template name is required");
          }
          if (!sections) {
            throw new Error("Configuration data is required");
          }

          await api("client-configuration-templates", {
            method: "POST",
            body: {
              templateName: String(templateName).trim(),
              sections,
              isDefault,
              defaultInsightsLens,
            },
          });

          flash("success", "Template created");
          res.refreshWith(
            {
              "modal[type]": "client-configuration-templates",
              currentConfigTemplateId: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/client-configuration/save-as-template",
      async ({ api, flash, req, res }) => {
        const { body } = req;
        const {
          templateName,
          sections,
          isDefault,
          defaultInsightsLens,
          applyToAthleteId,
        } = body || {};
        try {
          if (!templateName || String(templateName).trim() === "") {
            throw new Error("Template name is required");
          }
          if (!sections) {
            throw new Error("Configuration data is required");
          }

          const response = await api("client-configuration-templates", {
            method: "POST",
            body: {
              templateName: String(templateName).trim(),
              sections,
              isDefault,
              defaultInsightsLens,
            },
          });

          const createdTemplate = await response.json();

          if (applyToAthleteId) {
            await api(`client-configurations/${applyToAthleteId}/template`, {
              method: "PUT",
              body: {
                templateId: createdTemplate._id,
              },
            });
          }

          flash("success", "Template saved");
          res.backDontScroll();
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/client-configuration/update-template",
      async ({ api, flash, req, res }) => {
        const { body = {} } = req;
        const {
          templateId,
          templateName,
          sections,
          isDefault,
          defaultInsightsLens,
        } = body;
        try {
          if (!templateId) throw new Error("Template id is required");
          if (!templateName || String(templateName).trim() === "") {
            throw new Error("Template name is required");
          }
          if (!sections) throw new Error("Configuration data is required");

          await api(`client-configuration-templates/${templateId}`, {
            method: "PUT",
            body: {
              templateName: String(templateName).trim(),
              sections,
              isDefault,
              defaultInsightsLens,
            },
          });

          flash("success", "Template updated");
          res.refreshWith(
            {
              "modal[type]": "client-configuration-templates",
              currentConfigTemplateId: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/client-configuration/set-default-template",
      async ({ api, flash, req, res }) => {
        const { body = {} } = req;
        const { templateId } = body;
        try {
          if (!templateId) {
            throw new Error("Template id is required");
          }
          await api(`client-configuration-templates/${templateId}`, {
            method: "PUT",
            body: { isDefault: true },
          });
          flash("success", "Default template updated");
          res.backDontScroll();
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/client-configuration/unset-default-template",
      async ({ api, flash, req, res }) => {
        const { body = {} } = req;
        const { templateId } = body;
        try {
          if (!templateId) {
            throw new Error("Template id is required");
          }
          await api(`client-configuration-templates/${templateId}`, {
            method: "PUT",
            body: { isDefault: false },
          });
          flash("success", "Default template removed");
          res.backDontScroll();
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/client-configuration/prefetch-section-blocks",
      async ({ api, req, res, session }) => {
        const { clientIds } = req.body || {};
        if (!clientIds) {
          res.status = 400;
          res.body = { error: "clientIds required" };
          return;
        }
        try {
          const response = await api(
            "client-configurations/section-turn-off-validator/batch",
            { query: { clientIds } }
          );
          const data = await response.json();

          writeSectionBlocksCache(
            session,
            data.sectionBlocksByClientId,
            clientIds
          );
          res.status = 200;
          res.body = data;
        } catch {
          res.status = 500;
        }
      }
    )
    .post(
      "/client-configuration/fetch-configs-batch",
      async ({ api, req, res, session }) => {
        const { clientIds, filterGroup, organizationCoach } = req.body || {};
        if (!Array.isArray(clientIds) || !clientIds.length) {
          res.status = 400;
          res.body = { error: "clientIds required" };
          return;
        }
        try {
          const response = await api("client-configurations/list", {
            method: "POST",
            body: { clientIds, filterGroup, organizationCoach },
          });
          const data = await response.json();
          if (response.ok) {
            mergeConfigsByClientIdCache(session, data.configsByClientId);
          }
          res.status = response.ok ? 200 : response.status;
          res.body = data;
        } catch {
          res.status = 500;
        }
      }
    )
    .post(
      "/client-configuration/filter",
      async ({ api, req, res, session }) => {
        const {
          clientIds,
          filterGroup,
          organizationCoach,
          filtersJson,
          sectionBlocksJson,
          match,
        } = req.body || {};
        if (!Array.isArray(clientIds) || !clientIds.length) {
          res.status = 400;
          res.body = { error: "clientIds required" };
          return;
        }
        // "on" / "off" style options get turned into booleans twice on the way
        // to the API: our body parser runs castJSON on every field (js/app.js),
        // and api() sends object bodies as form fields, which the API casts
        // the same way. So filters arrive here JSON-encoded, and go on to the
        // API as a real JSON body (below), which neither side casts.
        let filters;
        try {
          filters = filtersJson ? JSON.parse(filtersJson) : {};
        } catch {
          res.status = 400;
          res.body = { error: "filtersJson must be valid JSON" };
          return;
        }
        let knownSectionBlocks;
        try {
          knownSectionBlocks = sectionBlocksJson
            ? JSON.parse(sectionBlocksJson)
            : undefined;
        } catch {
          knownSectionBlocks = undefined;
        }
        // Each Apply is a new request; cache per filter combination so
        // re-applying one already seen is instant (the list also writes it
        // into the browser's session copy — see rememberConfigListFilter).
        const cacheArgs = configListFilterCacheArgs({
          clientIds,
          filterGroup,
          organizationCoach,
          filters,
          match,
        });
        const cached = readConfigListFilterCache(session, cacheArgs);
        if (cached) {
          res.status = 200;
          res.body = cached;
          return;
        }
        try {
          // Configs and has-data flags the table already loaded go along,
          // so the API only computes what's missing.
          const response = await api("client-configurations/filter", {
            method: "POST",
            body: JSON.stringify({
              ...cacheArgs,
              knownConfigs: knownConfigsForFilter(session, clientIds),
              knownSectionBlocks,
            }),
            headers: { "Content-Type": "application/json" },
          });
          const data = await response.json();
          if (response.ok) {
            writeConfigListFilterCache(session, cacheArgs, data);
          }
          res.status = response.ok ? 200 : response.status;
          res.body = data;
        } catch {
          res.status = 500;
        }
      }
    )
    .post(
      "/client-configuration/delete-template",
      async ({ api, flash, req, res }) => {
        const { body = {} } = req;
        const { templateId } = body;
        try {
          if (!templateId) {
            throw new Error("Template id is required");
          }
          await api(`client-configuration-templates/${templateId}`, {
            method: "DELETE",
          });
          flash("success", "Template removed");
          res.backDontScroll();
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    );
};

export default ClientConfig;
